import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export interface ColumnOption {
  key: string
  label: string
  // false pins the row: it can still be hidden, but it never drags and
  // ignores the reorder chords (LG-362 §3 — the external id is attached to
  // the title, so it owns no grid track for an order to apply to).
  movable?: boolean
}

export function ColumnMenu(props: {
  position: { x: number; y: number }
  columns: readonly ColumnOption[]
  hiddenCols: string[]
  onToggle: (column: string) => void
  // When set, rows can be reordered by dragging or Alt+Arrow keys. The
  // dragged column key is moved to the given index within the *movable*
  // subset of `columns` (rows with `movable: false` sit in the menu but not
  // in the caller's order array, so they own no index).
  onReorder?: (column: string, toIndex: number) => void
  onClose: () => void
}): ReactNode {
  const { position, columns, hiddenCols, onToggle, onReorder, onClose } = props
  const menuRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  // Reordering moves the focused row's DOM node, which blurs it in real
  // browsers; restore focus to the moved row after React commits.
  const pendingReorderFocus = useRef(false)
  // The menu measures itself to stay inside the viewport, so callers only
  // supply the raw anchor point and the menu can change shape freely.
  const [clampedPosition, setClampedPosition] = useState(position)

  // `onReorder` indices count only movable columns — pinned rows sit in the
  // menu but not in the caller's order array, so menu indices must be
  // translated before they leave this component.
  const movableColumns = columns.filter((column) => column.movable !== false)
  const movableIndexAt = (index: number): number =>
    columns.slice(0, index).filter((column) => column.movable !== false).length

  useEffect(() => {
    menuRef.current
      ?.querySelector<HTMLElement>('[role="menuitemcheckbox"]')
      ?.focus()
  }, [])

  useLayoutEffect(() => {
    const rect = menuRef.current?.getBoundingClientRect()
    if (!rect) return
    setClampedPosition({
      x: Math.max(8, Math.min(position.x, window.innerWidth - rect.width - 8)),
      y: Math.max(
        8,
        Math.min(position.y, window.innerHeight - rect.height - 8),
      ),
    })
  }, [position])

  useLayoutEffect(() => {
    if (!pendingReorderFocus.current) return
    pendingReorderFocus.current = false
    const items = menuRef.current?.querySelectorAll<HTMLElement>(
      '[role="menuitemcheckbox"]',
    )
    items?.[activeIndex]?.focus()
  })

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitemcheckbox"]',
      ) ?? [],
    )
    if (items.length === 0) return

    const focusedIndex = items.indexOf(document.activeElement as HTMLElement)
    const currentIndex = focusedIndex >= 0 ? focusedIndex : activeIndex

    if (
      onReorder &&
      event.altKey &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      const column = columns[currentIndex]
      if (!column || column.movable === false) return
      const movableIndex = movableIndexAt(currentIndex)
      const targetIndex =
        event.key === 'ArrowDown' ? movableIndex + 1 : movableIndex - 1
      if (targetIndex < 0 || targetIndex >= movableColumns.length) return
      // Adjacent movable swap: the moved row lands in the menu slot its
      // neighbour holds now, which is where focus must follow.
      const neighbour = movableColumns[targetIndex]
      if (!neighbour) return
      pendingReorderFocus.current = true
      onReorder(column.key, targetIndex)
      setActiveIndex(columns.indexOf(neighbour))
      return
    }

    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (currentIndex + 1) % items.length
            : (currentIndex - 1 + items.length) % items.length

    setActiveIndex(nextIndex)
    items[nextIndex]?.focus()
  }

  const endDrag = (): void => {
    setDragKey(null)
    setDragOverIndex(null)
  }

  return (
    <>
      <button
        type="button"
        aria-label="Close column menu"
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default border-0 bg-transparent p-0"
      />
      <div
        ref={menuRef}
        role="menu"
        aria-label="Show columns"
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          if (
            event.relatedTarget instanceof Element &&
            !menuRef.current?.contains(event.relatedTarget)
          ) {
            onClose()
          }
        }}
        className="fixed z-50 min-w-40 rounded-lg border border-(--cd-border-2) bg-(--cd-card) p-2 shadow-2xl"
        style={{ left: clampedPosition.x, top: clampedPosition.y }}
      >
        <p className="px-2 pb-1 text-[9px] font-bold tracking-[0.08em] text-(--cd-text-faint)">
          SHOW COLUMNS
        </p>
        {columns.map((column, index) => {
          const hidden = hiddenCols.includes(column.key)
          const movable = onReorder !== undefined && column.movable !== false
          return (
            <button
              key={column.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={!hidden}
              tabIndex={index === activeIndex ? 0 : -1}
              aria-label={column.label}
              aria-keyshortcuts={
                movable ? 'Alt+ArrowUp Alt+ArrowDown' : undefined
              }
              draggable={movable}
              onFocus={() => setActiveIndex(index)}
              onClick={() => onToggle(column.key)}
              onDragStart={(event) => {
                // jsdom (and some AT) can fire dragstart despite
                // draggable=false, so the pinned rows refuse here too.
                if (!movable) return
                event.dataTransfer?.setData('text/plain', column.key)
                setDragKey(column.key)
              }}
              onDragOver={(event) => {
                if (dragKey === null) return
                event.preventDefault()
                setDragOverIndex(index)
              }}
              onDrop={(event) => {
                if (dragKey === null || onReorder === undefined) return
                event.preventDefault()
                if (dragKey !== column.key) {
                  pendingReorderFocus.current = true
                  onReorder(dragKey, movableIndexAt(index))
                  setActiveIndex(index)
                }
                endDrag()
              }}
              onDragEnd={endDrag}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs text-(--cd-text-muted) hover:bg-(--cd-inset) hover:text-(--cd-text) ${
                dragOverIndex === index && dragKey !== column.key
                  ? 'bg-(--cd-inset)'
                  : ''
              }`}
            >
              {onReorder ? (
                <span
                  aria-hidden="true"
                  // Pinned rows keep the grip's footprint so the checkboxes
                  // stay in one column, but offer nothing to grab.
                  className={
                    movable ? 'cursor-grab text-(--cd-text-faint)' : 'invisible'
                  }
                >
                  ⠿
                </span>
              ) : null}
              <span aria-hidden="true">{hidden ? '☐' : '☑'}</span>
              {column.label}
            </button>
          )
        })}
        {onReorder ? (
          <p className="px-2 pt-1 text-[9px] text-(--cd-text-faint)">
            Drag or Alt+↑/↓ to reorder
          </p>
        ) : null}
      </div>
    </>
  )
}
